import TurndownService from "turndown";
import { marked } from "marked";

const turndownService = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
});

/** Converts a note's stored HTML content into a Markdown string. */
export function htmlToMarkdown(html: string): string {
  if (!html) return "";
  return turndownService.turndown(html).trim();
}

/** Converts a Markdown string into HTML suitable for the contentEditable note body. */
export function markdownToHtml(markdown: string): string {
  return (marked.parse(markdown, { breaks: true }) as string).trim();
}

// Clipboard text from phones and chat apps often has Windows line endings,
// non-breaking spaces, or fancy bullet characters. Clean those up first so
// the Markdown detector and parser see ordinary Markdown.
function normalizeText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/^(\s*)[\u2022\u00b7\u25aa\u25e6\u2023\u2043]\s+/gm, "$1- ");
}

// Heuristics used to detect that pasted plain text is actually Markdown,
// so notes can auto-convert it instead of inserting it as raw text.
const MARKDOWN_PATTERNS: RegExp[] = [
  /^#{1,6}\s+\S/m, // headings
  /^\s*[-*+]\s+\S/m, // bullet lists
  /^\s*\d+[.)]\s+\S/m, // numbered lists
  /^\s*-\s?\[[ xX]\]\s+\S/m, // checkboxes
  /\*\*(?!\s)[^*\n]+?(?<!\s)\*\*/, // bold with **
  /__(?!\s)[^_\n]+?(?<!\s)__/, // bold with __
  /(?<![*\w])\*(?![\s*])[^*\n]+?(?<![\s*])\*(?![*\w])/, // italic with *
  /(?<![_\w])_(?![\s_])[^_\n]+?(?<![\s_])_(?![_\w])/, // italic with _
  /~~[^~\n]+~~/, // strikethrough
  /`[^`\n]+`/, // inline code
  /\[[^\]\n]+\]\([^)\n]+\)/, // links
  /^>\s+\S/m, // quotes
  /^(```|~~~)/m, // fenced code block
];

function countMarkdownHits(text: string): number {
  return MARKDOWN_PATTERNS.filter((pattern) => pattern.test(text)).length;
}

export function looksLikeMarkdown(text: string): boolean {
  return countMarkdownHits(normalizeText(text)) > 0;
}

// Tags that mean the clipboard really carries formatted text (copied from a
// web page, for example) rather than plain text wrapped in a span or div.
const RICH_HTML = /<(b|strong|i|em|u|s|strike|del|ul|ol|li|h[1-6]|a|blockquote|pre|code|table|img|hr)[\s>/]/i;

/**
 * Looks at a paste and, if it is Markdown, returns it converted to HTML.
 * Returns null when the paste should be handled normally.
 */
export function markdownFromClipboard(data: DataTransfer): string | null {
  const raw = data.getData("text/plain");
  if (!raw) return null;
  const text = normalizeText(raw);
  const hits = countMarkdownHits(text);
  if (hits === 0) return null;
  const html = data.getData("text/html");
  // Real formatted text on the clipboard: only convert when the plain text
  // clearly is Markdown (two or more different Markdown signs).
  if (html && RICH_HTML.test(html) && hits < 2) return null;
  return markdownToHtml(text);
}

/** Converts saved plain text to HTML if it is Markdown; leaves HTML and normal text alone. */
export function markdownIfPlain(content: string): string {
  if (!content || /<[a-z][\s\S]*>/i.test(content)) return content;
  const text = normalizeText(content);
  return countMarkdownHits(text) > 0 ? markdownToHtml(text) : plainToHtml(content);
}
export function plainToHtml(t: string): string { if (!/\n/.test(t)) return t; return t.split(/\r?\n/).map((l) => "<p>" + (l.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") || "<br>") + "</p>").join(""); }
